import { ColumnDef, flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { useTranslation } from 'react-i18next';
import { ETablePagination } from '@/components/everridge/table/components/ETablePagination';

type Props<T> = {
    columns: ColumnDef<T, unknown>[];
    data: T[];
    pageSize?: number;
};

const SuperAdminDataPaginatedTable = <T,>({ columns, data, pageSize = 10 }: Props<T>) => {
    const { t } = useTranslation();

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize,
            },
        },
    });

    return (
        <div className="rounded-semi-xl border border-popover-100 bg-popover-10">
            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHead key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.length ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="py-6 text-center text-sm font-medium">
                                {t('elements.table.body.empty')}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {table.getPageCount() > 1 && (
                <div className="border-t border-popover-100 py-4">
                    <ETablePagination table={table} />
                </div>
            )}
        </div>
    );
};

export default SuperAdminDataPaginatedTable;
